import { InfiniteSelect, Select } from '@common-web/index.js';
import { formatOptionsForSelect } from '@utils/utils';
import { Flex } from 'antd';
import { useMemo, useState, useEffect } from 'react';

const EMPTY_PAGINATION = { current_page: 1, next_page: null };

const ensureFormattedOptions = ({ options = [], setAsSlug = false }) => {
  if (!options?.length) {
    return [];
  }

  if (options[0]?.label !== undefined && options[0]?.value !== undefined) {
    return options;
  }

  return formatOptionsForSelect({ list: options, setAsSlug });
};

const resolveStaticChildOptions = ({ parentValue, parentOptions, childConfig }) => {
  if (!parentValue) {
    return [];
  }

  if (childConfig?.optionsMap) {
    const optionsMap = childConfig.optionsMap;
    if (optionsMap instanceof Map) {
      return optionsMap.get(parentValue) ?? [];
    }
    if (typeof optionsMap === 'object') {
      return optionsMap?.[parentValue] ?? [];
    }
  }

  if (childConfig?.options) {
    const parentField = childConfig.parentField ?? 'parent_id';
    return childConfig.options.filter((option) => String(option?.[parentField]) === String(parentValue));
  }

  const parentMatch = parentOptions.find((option) => {
    const matches = String(option?.value ?? option?.id ?? option?.slug) === String(parentValue) ||
      String(option?.slug) === String(parentValue);
    return matches;
  });
  if (parentMatch?.children) {
    return parentMatch.children;
  }

  return [];
};

const parseStoredValues = ({ value, mode }) => {
  if (!value) {
    return [];
  }
  const isMultiple = (mode ?? 'single') === 'multiple';
  return isMultiple ? value.split(',').filter(Boolean) : [value];
};

const deriveSelectValue = ({ storedValue, options = [], setAsSlug, mode, key }) => {
  const storedValues = parseStoredValues({ value: storedValue, mode });
  if (!storedValues.length || !options.length) {
    return (mode ?? 'single') === 'multiple' ? [] : undefined;
  }

  const resolved = storedValues
    .map((token) => {
      const match = options.find((option) => {
        if (key && String(option?.[key]) === String(token)) return true;
        const slugCandidate = option?.slug ?? option?.value ?? option?.id;
        const valueCandidate = option?.value ?? option?.id ?? option?.slug;
        return setAsSlug ? String(slugCandidate) === String(token) : String(valueCandidate) === String(token);
      });
      if (!match) {
        return undefined;
      }
      const antValue = setAsSlug ? match?.slug : (match?.value ?? match?.id);
      return antValue !== undefined ? antValue.toString() : undefined;
    })
    .filter(Boolean);

  if (!resolved.length) {
    return (mode ?? 'single') === 'multiple' ? [] : undefined;
  }

  return (mode ?? 'single') === 'multiple' ? resolved : resolved[0];
};

const CascadingDropdown = ({ item = {}, filtersObject = {}, setFiltersObject = () => { }, loading = false }) => {
  const parentConfig = item.parent ?? {};
  const childConfig = item.child ?? {};
  const parentKey = parentConfig.key;
  const childKey = childConfig.key;

  const parentValue = filtersObject[parentKey];
  const childValue = filtersObject[childKey];

  const [resetChildToggle, setResetChildToggle] = useState(false);

  const formattedParentOptions = useMemo(
    () => ensureFormattedOptions({ options: parentConfig.options, setAsSlug: parentConfig.setAsSlug }),
    [parentConfig.options, parentConfig.setAsSlug]
  );

  const staticChildOptions = useMemo(() => {
    // If child options are already set (from parent selection), use them directly
    if (childConfig.options && childConfig.options.length > 0) {
      return ensureFormattedOptions({ options: childConfig.options, setAsSlug: childConfig.setAsSlug });
    }

    // Otherwise, try to resolve from parent
    const derivedOptions = resolveStaticChildOptions({
      parentValue,
      parentOptions: formattedParentOptions,
      childConfig,
    });
    return ensureFormattedOptions({ options: derivedOptions, setAsSlug: childConfig.setAsSlug });
  }, [childConfig, formattedParentOptions, parentValue]);

  const parentMode = parentConfig.mode ?? 'single';
  const childMode = childConfig.mode ?? 'multiple';

  const shouldShowParentLabel = parentConfig.showLabel ?? item.isAdvancedFilter ?? false;
  const shouldShowChildLabel = childConfig.showLabel ?? item.isAdvancedFilter ?? false;

  const parentSelectValue = deriveSelectValue({
    storedValue: parentValue,
    options: formattedParentOptions,
    setAsSlug: parentConfig.setAsSlug,
    mode: parentMode,
  });

  const childSelectValue = deriveSelectValue({
    storedValue: childValue,
    options: staticChildOptions,
    setAsSlug: childConfig.setAsSlug,
    mode: childMode,
    key: childConfig.key,
  });

  const updateParentValue = (selectedOption) => {
    const optionValue = Array.isArray(selectedOption)
      ? selectedOption[selectedOption.length - 1]
      : selectedOption;
    const nextFilters = { ...filtersObject };

    if (optionValue) {
      const value = parentConfig.setAsSlug
        ? (optionValue?.slug ?? optionValue?.value)
        : (optionValue?.id ?? optionValue?.value);
      nextFilters[parentKey] = value?.toString();

      // Update child options from parent's children
      if (optionValue?.children && childConfig.setter) {
        // Ensure children are properly formatted
        const formattedChildren = ensureFormattedOptions({
          options: optionValue.children,
          setAsSlug: childConfig.setAsSlug
        });
        childConfig.setter(formattedChildren);
      }
    } else {
      delete nextFilters[parentKey];
    }

    if (childKey) {
      delete nextFilters[childKey];
      if (!optionValue?.children) {
        childConfig.setter?.([]);
      }
      setResetChildToggle((prev) => !prev);
    }

    if (nextFilters.page) {
      delete nextFilters.page;
    }
    setFiltersObject(nextFilters);
  };

  const updateChildValue = (selectedOptions = []) => {
    const optionsArray = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];
    const nextFilters = { ...filtersObject };

    if (optionsArray?.length) {
      const valueArray = optionsArray
        .map((option) => {
          const reference = childConfig.setAsSlug ? (option?.slug ?? option?.value) : (option?.id ?? option?.value);
          return reference !== undefined ? reference.toString() : undefined;
        })
        .filter(Boolean);
      nextFilters[childKey] = valueArray.join(',');
    } else {
      delete nextFilters[childKey];
    }

    if (nextFilters.page) {
      delete nextFilters.page;
    }
    setFiltersObject(nextFilters);
  };

  const getChildFetch = () => {
    if (!childConfig.fetch) {
      return undefined;
    }

    const fetchKey = childConfig.fetchKey;
    if (!fetchKey) {
      return undefined;
    }

    return (params = '') => {
      if (!parentValue) {
        return Promise.resolve({ [fetchKey]: [], pagination: EMPTY_PAGINATION });
      }

      const sanitizedParams = params ? params.replace('?', '&') : '';
      const builtQuery = childConfig.buildQuery
        ? childConfig.buildQuery({ parentValue, params: sanitizedParams })
        : `${parentValue}${sanitizedParams}`;
      const extraArgs = childConfig.extraArgs ?? [];
      return childConfig.fetch(builtQuery, ...extraArgs);
    };
  };

  const parentUsesInfiniteSelect = !!parentConfig.fetch;
  const childUsesInfiniteSelect = !!childConfig.fetch;

  const parentLabel = parentConfig.label ?? item.label;
  const parentPlaceholder = parentConfig.placeholder ?? item.placeholder;
  const childLabel = childConfig.label;
  const childPlaceholder = childConfig.placeholder;

  const childDisabled = !parentValue;

  const childFetch = getChildFetch();

  function childValues(values, options) {
    const arr = normalizeToArray(values);

    return options?.filter((option) =>
      arr?.some(
        (v) =>
          String(v) === String(option.slug) ||
          String(v) === String(option.id) ||
          String(v) === String(option.value) ||
          (childConfig?.key && String(v) === String(option[childConfig.key]))
      )
    );
  }

  function normalizeToArray(val) {
    if (Array.isArray(val)) return val;
    if (val == null) return [];
    return [val];
  }

  return (
    <Flex gap={8} wrap="nowrap" align="start" style={{ width: '100%' }}>
      <div style={parentConfig.wrapperStyle ?? { flex: '1 1 auto', minWidth: 160, maxWidth: '50%' }}>
        {parentUsesInfiniteSelect ? (
          <InfiniteSelect
            label={shouldShowParentLabel ? parentLabel : undefined}
            placeholder={parentPlaceholder}
            fetch={parentConfig.fetch}
            fetchKey={parentConfig.fetchKey}
            searchKey={parentConfig.searchKey}
            queryKey={parentConfig.queryKey}
            filterKey={parentKey}
            mode={parentMode}
            defaultValue={parentValue ?? null}
            setter={parentConfig.setter}
            options={parentConfig.options}
            loading={loading}
            setAsSlug={parentConfig.setAsSlug}
            onChange={(selected) => updateParentValue(selected)}
            disabled={parentConfig.disabled}
            allowClear={true}
            clearSelect={() => updateParentValue([])}
          />
        ) : (
          <Select
            label={shouldShowParentLabel ? parentLabel : undefined}
            placeholder={parentPlaceholder}
            mode={parentMode}
            options={formattedParentOptions}
            value={parentMode === 'multiple' ? parentSelectValue : parentSelectValue ?? undefined}
            loading={loading}
            onChange={(selection) => updateParentValue(selection)}
            onClear={() => updateParentValue(null)}
            allowClear={true}
          />
        )}
      </div>

      {childKey ? (
        <div style={childConfig.wrapperStyle ?? { flex: '1 1 auto', minWidth: 180, maxWidth: '50%' }}>
          {childUsesInfiniteSelect ? (
            <InfiniteSelect
              label={shouldShowChildLabel ? childLabel : undefined}
              placeholder={childPlaceholder}
              fetch={childFetch}
              fetchKey={childConfig.fetchKey}
              searchKey={childConfig.searchKey}
              queryKey={childConfig.queryKey}
              filterKey={childKey}
              mode={childMode}
              defaultValue={childValue ?? null}
              setter={childConfig.setter}
              options={childConfig.options}
              loading={loading}
              customRender={childConfig.customRender}
              setAsSlug={childConfig.setAsSlug}
              resetValue={resetChildToggle}
              disabled={childDisabled}
              onChange={(values) => updateChildValue(Array.isArray(values) ? values : [values])}
              allowClear={true}
            />
          ) : (
            <Select
              label={shouldShowChildLabel ? childLabel : undefined}
              placeholder={childPlaceholder}
              mode={childMode}
              options={staticChildOptions}
              // disabled={childDisabled}
              value={childMode === 'multiple' ? childValues(childSelectValue, staticChildOptions) : childValues(childSelectValue, staticChildOptions) ?? undefined}
              loading={loading}
              onChange={(selected) => updateChildValue(selected ?? [])}
              onClear={() => updateChildValue([])}
              allowClear={true}
            />
          )}
        </div>
      ) : null}
    </Flex>
  );
};

export default CascadingDropdown;
